import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Modal, Typography, Button } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { lightGrey, white } from '../../../app/theme';
import { setPasswordAPIcall } from '../../../redux/actions/auth/auth.actions';
import TextFieldWrapper from '../TextFieldWrapper/TextFieldWrapper';

interface Props {
  isOpen: boolean;
  handleModalOpen: (isOpen: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  modalContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    maxWidth: '70%',
    padding: '2em',
    position: 'relative',
    boxSizing: 'border-box',
    backgroundColor: white,
    borderRadius: 5,
  },
  title: {
    paddingBottom: 5,
    marginBottom: 20,
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    fontSize: 25,
    fontWeight: 600,
  },
  formBody: {
    display: 'flex',
    flexDirection: 'row',
  },
  fields: {
    minWidth: 250,
    marginRight: '2em',
    display: 'flex',
    flexDirection: 'column',
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  control: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 5,
  },
  passwordInfo: {
    fontSize: 16,
    margin: 0,
  },
  list: {
    margin: 0,
    padding: 0,
    '& > li': {
      fontSize: 16,
      marginLeft: '1em',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    borderRadius: 21,
    height: 42,
    minWidth: 104,
    marginLeft: 37,
  },
}));

const passwordSchema = Yup.object({
  password: Yup.string()
    .required('*Required field')
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[A-Z])/, 'Password must include at least one uppercase character')
    .matches(/^(?=.*[a-z])/, 'Password must include at least one lowercase character')
    .matches(/^(?=.*[0-9])/, 'Password must include at least one number')
    .matches(/^(?=.*[`~!@#$%^&*()\-_=+{}[\]\\|;:'",./?])/, 'Password must include at least one symbol')
    .test('No angle brackest', 'Password may not contain < or >', (value) => {
      return value && value.indexOf('<') < 0 && value.indexOf('>') < 0;
    }),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('*Required field'),
});

const PasswordModal = ({ isOpen, handleModalOpen }: Props): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const defaultFormValues = {
    password: '',
    confirmPassword: '',
  };

  const handleSubmit = (formValues: { password: string; confirmPassword: string }) => {
    handleModalOpen(false);
    dispatch(setPasswordAPIcall(formValues.password));
  };

  return (
    <Modal open={isOpen} onClose={() => handleModalOpen(false)} disableBackdropClick disableEscapeKeyDown>
      <div className={classes.modalContainer}>
        <div className={classes.modal}>
          <Formik
            initialValues={defaultFormValues}
            validationSchema={passwordSchema}
            onSubmit={(values) => handleSubmit(values)}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {(formProps) => (
              <form onSubmit={formProps.handleSubmit}>
                <Typography className={classes.title}>Change Password</Typography>
                <div className={classes.formBody}>
                  <div className={classes.fields}>
                    <div className={classes.control}>
                      <TextFieldWrapper
                        label="PASSWORD*"
                        inputLabelProps={{ className: classes.inputField }}
                        form={formProps}
                        formKey="password"
                        type="password"
                        variant="outlined"
                      />
                    </div>
                    <div className={classes.control}>
                      <TextFieldWrapper
                        label="CONFIRM PASSWORD*"
                        inputLabelProps={{ className: classes.inputField }}
                        form={formProps}
                        formKey="confirmPassword"
                        type="password"
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className={classes.passwordInfo}>
                    <Typography>Your password must include at least:</Typography>
                    <ul className={classes.list}>
                      <li>Eight characters</li>
                      <li>One uppercase character</li>
                      <li>One lowercase character</li>
                      <li>One number</li>
                      <li>One special character, except &lt; and &gt;</li>
                    </ul>
                  </div>
                </div>
                <div className={classes.buttonContainer}>
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="secondary"
                    onClick={() => handleModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button className={classes.button} variant="contained" color="secondary" type="submit">
                    Save
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default PasswordModal;
